/**
 * platform-auth-store.ts — Zustand store cho danh tính super-admin (Platform).
 *
 * Danh tính này TÁCH BIỆT với `auth-store` của người dùng CRM: gọi các endpoint
 * `/platform/auth/*` và dùng token riêng qua `platformApi`.
 */
import { create } from 'zustand'
import {
  platformApi,
  clearPlatformTokens,
  setPlatformTokens,
  getPlatformToken,
} from '@/lib/platform-client'
import { queryClient } from '@/lib/query-client'
import { withTurnstileToken } from '@/lib/turnstile'
import type { TotpChallenge, TotpInfo } from '@/types/api'

export interface PlatformAdmin {
  id: string
  email: string
  fullName: string
  isActive?: boolean
  lastLoginAt?: string | null
  createdAt?: string
  /** Xác thực 2 lớp — LUÔN mandatory=true cho PlatformAdmin (Nghị định 356/2025/NĐ-CP, Điều 9.3). */
  totp?: TotpInfo
}

interface PlatformAuthState {
  admin: PlatformAdmin | null
  loading: boolean
  /** Trả `TotpChallenge` khi mật khẩu đúng nhưng còn thiếu bước TOTP — xem platform-auth-service.ts#PlatformLoginOutcome. */
  login: (email: string, password: string, turnstileToken?: string | null) => Promise<PlatformAdmin | TotpChallenge>
  logout: () => Promise<void>
  loadMe: () => Promise<void>
  setAdmin: (a: PlatformAdmin | null) => void
  /** Hoàn tất đăng nhập — tài khoản ĐÃ bật TOTP. */
  completeTotpLogin: (ticket: string, code: string) => Promise<PlatformAdmin>
  /** Bắt buộc thiết lập (mọi PlatformAdmin) — bước 1: sinh secret + QR. */
  startTotpLoginSetup: (ticket: string) => Promise<{ otpauthUri: string; qrDataUrl: string }>
  /** Bắt buộc thiết lập — bước 2: xác nhận mã đầu → bật + đăng nhập + 10 mã dự phòng (hiện 1 lần). */
  confirmTotpLoginSetup: (ticket: string, code: string) => Promise<{ user: PlatformAdmin; backupCodes: string[] }>
}

export const usePlatformAuthStore = create<PlatformAuthState>((set) => ({
  admin: null,
  loading: true,

  login: async (email, password, turnstileToken) => {
    const { data } = await platformApi.post('/platform/auth/login', withTurnstileToken({ email, password }, turnstileToken))
    // Xác thực 2 lớp: BẮT BUỘC cho mọi PlatformAdmin — mật khẩu đúng chưa đủ, chỉ có vé tạm (10 phút).
    if (data.totpRequired) return { totpRequired: true, mode: data.mode, ticket: data.ticket } as TotpChallenge
    setPlatformTokens(data.token, data.refreshToken)
    // `admin` từ login là payload JWT (id nằm ở `sub`); tải profile đầy đủ ngay sau.
    const me = await platformApi.get('/platform/auth/me')
    set({ admin: me.data, loading: false })
    return me.data
  },

  logout: async () => {
    try {
      await platformApi.post('/platform/auth/logout')
    } catch {
      /* bỏ qua lỗi mạng khi đăng xuất */
    }
    clearPlatformTokens()
    // Super-admin kế tiếp (cùng trình duyệt, trang vẫn mở) không được thấy dữ liệu đã nạp của người này (xem auth-store: dropIdentityData).
    void queryClient.cancelQueries()
    queryClient.clear()
    set({ admin: null })
  },

  loadMe: async () => {
    if (!getPlatformToken()) {
      set({ admin: null, loading: false })
      return
    }
    try {
      const { data } = await platformApi.get('/platform/auth/me')
      set({ admin: data, loading: false })
    } catch {
      set({ admin: null, loading: false })
    }
  },

  setAdmin: (a) => set({ admin: a }),

  completeTotpLogin: async (ticket, code) => {
    const { data } = await platformApi.post('/platform/auth/totp/login-verify', { ticket, code })
    setPlatformTokens(data.token, data.refreshToken)
    const me = await platformApi.get('/platform/auth/me')
    set({ admin: me.data, loading: false })
    return me.data
  },

  startTotpLoginSetup: async (ticket) => {
    const { data } = await platformApi.post('/platform/auth/totp/login-setup/start', { ticket })
    return data
  },

  confirmTotpLoginSetup: async (ticket, code) => {
    const { data } = await platformApi.post('/platform/auth/totp/login-setup/confirm', { ticket, code })
    setPlatformTokens(data.token, data.refreshToken)
    const me = await platformApi.get('/platform/auth/me')
    set({ admin: me.data, loading: false })
    return { user: me.data, backupCodes: data.backupCodes }
  },
}))
